'use client';

import { useCallback, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';

import { clamp } from '@/modules/captcha/shared/puzzle-utils';

interface UsePuzzleSliderOptions {
  /**
   * Decides the attempt when the handle is released, given progress in 0..1. Return true to
   * accept. Slide-to-end puzzles check `>= 0.92`; precision puzzles check alignment.
   */
  onRelease: (progress: number) => boolean;
  /** Progress the handle snaps to on acceptance. Defaults to wherever it was released. */
  snapTo?: (progress: number) => number;
  /**
   * Whether a rejected attempt springs the handle back to the start. Defaults to true.
   *
   * True for slide-to-end, where the answer is always the far end and a handle stranded
   * mid-track reads as "nearly there". False for the precision puzzle, where the customer aimed
   * at a specific spot — throwing their position away on a near miss forces them to re-aim from
   * scratch every attempt, and makes the puzzle effectively unpassable by keyboard, where
   * each press is a deliberate step rather than one continuous gesture.
   */
  resetOnReject?: boolean;
}

/** Track is 312px wide with a 44px handle, so the handle travels 266px + 2px of border. */
export const SLIDER_TRAVEL = 266;

/** Arrow-key increment. Coarse for slide-to-end, but the precision puzzle overrides it. */
const KEY_STEP = 0.08;

/**
 * Drag-a-handle-along-a-track behaviour for the slider puzzles (1c, 1d).
 *
 * ## Invariants:
 * - Progress is measured against the track's live `getBoundingClientRect()`, captured on
 *   pointerdown. Why: the modal is a bottom sheet that animates in, and a rect measured at mount
 *   is stale by the time the customer touches it — every drag would then be offset by the
 *   animation distance.
 * - A rejected release returns the handle to zero rather than leaving it where it was dropped.
 *   Why: a handle stranded at 80% reads as "nearly right, nudge it", which is wrong for
 *   slide-to-end (the answer is always the far end) and misleading for precision (the answer is
 *   a specific spot, not further along).
 */
export function usePuzzleSlider({
  onRelease,
  snapTo,
  resetOnReject = true,
}: UsePuzzleSliderOptions) {
  const trackRef = useRef<HTMLDivElement>(null);
  const trackRect = useRef<DOMRect | null>(null);
  const [progress, setProgress] = useState(0);
  const [sliding, setSliding] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const settle = useCallback(
    (at: number, viaKeyboard = false) => {
      setSliding(false);
      if (onRelease(at)) {
        setAccepted(true);
        setProgress(snapTo ? snapTo(at) : at);
        return;
      }
      // Only a rejected POINTER release springs back. A keyboard commit is a deliberate step in
      // a sequence the customer is building up one press at a time, and wiping it would leave
      // them stepping forward and being reset forever — on slide-to-end, permanently, since the
      // handle can then never accumulate enough presses to reach the far end.
      if (resetOnReject && !viaKeyboard) setProgress(0);
    },
    [onRelease, resetOnReject, snapTo]
  );

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (accepted) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      trackRect.current = trackRef.current?.getBoundingClientRect() ?? null;
      setSliding(true);
    },
    [accepted]
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!sliding || accepted || !trackRect.current) return;
      const offset = event.clientX - trackRect.current.left - 22;
      setProgress(clamp(offset / SLIDER_TRAVEL, 0, 1));
    },
    [accepted, sliding]
  );

  const onPointerUp = useCallback(() => {
    if (!sliding || accepted) return;
    settle(progress);
  }, [accepted, progress, settle, sliding]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (accepted) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const step = event.key === 'ArrowRight' ? KEY_STEP : -KEY_STEP;
        setProgress((prev) => {
          const next = clamp(prev + step, 0, 1);
          // Slide-to-end must be reachable by keyboard alone: releasing is implicit at the far
          // end, otherwise a keyboard user could never trigger the check.
          if (next >= 1) settle(1, true);
          return next;
        });
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        settle(progress, true);
      }
    },
    [accepted, progress, settle]
  );

  return {
    trackRef,
    progress,
    sliding,
    accepted,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onKeyDown },
  };
}
